import { X, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import {
  validateAnalyticsFilters,
  type FilterWarning,
} from '../../../../hooks/analytics/validation';

interface FilterToastNotificationProps {
  filters: BaseAnalyticsFilter;
}

export default function FilterToastNotification({ filters }: FilterToastNotificationProps) {
  const [dismissed, setDismissed] = useState(false);
  const [currentWarnings, setCurrentWarnings] = useState<FilterWarning[]>([]);

  useEffect(() => {
    const validation = validateAnalyticsFilters(filters);
    setCurrentWarnings(validation.warnings);
    setDismissed(false);
  }, [filters]);

  useEffect(() => {
    if (currentWarnings.length > 0) {
      const timer = setTimeout(() => setDismissed(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [currentWarnings]);

  if (currentWarnings.length === 0 || dismissed) {
    return null;
  }

  const highestSeverity = currentWarnings.reduce((max, w) => {
    const severityOrder = { info: 1, warning: 2, heavy: 3 };
    const maxOrder = severityOrder[max];
    const currentOrder = severityOrder[w.severity];
    return currentOrder > maxOrder ? w.severity : max;
  }, currentWarnings[0].severity);

  const getStyles = () => {
    switch (highestSeverity) {
      case 'heavy':
        return {
          bg: 'bg-orange-50/95',
          border: 'border-orange-300',
          text: 'text-orange-800',
          icon: 'text-orange-600',
          Icon: AlertTriangle,
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50/95',
          border: 'border-yellow-300',
          text: 'text-yellow-800',
          icon: 'text-yellow-600',
          Icon: AlertCircle,
        };
      default:
        return {
          bg: 'bg-blue-50/95',
          border: 'border-blue-300',
          text: 'text-blue-800',
          icon: 'text-blue-600',
          Icon: Info,
        };
    }
  };

  const styles = getStyles();
  const Icon = styles.Icon;

  return (
    <div className="pointer-events-none fixed right-4 top-20 z-50 flex items-start">
      <div
        className={`pointer-events-auto flex max-w-xs rounded-md border ${styles.border} ${styles.bg} p-2.5 shadow-md backdrop-blur-sm`}
      >
        <Icon className={`h-4 w-4 shrink-0 ${styles.icon}`} />
        <div className="ml-2 flex-1">
          <div className={`text-xs ${styles.text}`}>
            {currentWarnings.length === 1 ? (
              <p>{currentWarnings[0].message}</p>
            ) : (
              <ul className="list-disc space-y-0.5 pl-3">
                {currentWarnings.map((warning, index) => (
                  <li key={index}>{warning.message}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className={`ml-2 shrink-0 rounded ${styles.text} opacity-60 hover:opacity-100 focus:outline-none`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
